<script setup lang="ts">
import { getAssetsUrlForCDN } from '@/utils/template';
import { darken, lighten } from '@/utils/apostatudo/color';
import type { RoulettePrize, RouletteRingCustom } from '@/types/roulette';

type Props = {
  prizes: RoulettePrize[];
  ringCustom?: RouletteRingCustom | null;
  spinDurationMs?: number;
};

const { prizes, ringCustom, spinDurationMs } = defineProps<Props>();

// Emite 'tick' a cada divisória que passa pelo ponteiro (efeito "clac" no pino).
const emit = defineEmits<{ tick: [] }>();

const CENTER = 100;
// Fatias preenchem até o aro (SLICE_RADIUS = raio cheio do viewBox 0..100).
const SLICE_RADIUS = 100;
// Âncora-base do rótulo perto do aro; o core ajusta fonte e recuo por prêmio
// (layout.textScale / text2Scale / textOffsetX / textOffsetY).
const TITLE_RADIUS = 90;
// Imagem recortada na cunha (clip) — preenche a fatia como no Figma. Tamanho e
// recuo vêm do core (layout.imageScale / imageOffsetX / imageOffsetY); estes são
// só a base 100% ancorada no raio IMAGE_RADIUS.
const IMAGE_RADIUS = 33;
const IMAGE_W = 80;
const IMAGE_H = 80;
// O core envia layout (fonte/recuo) no espaço de design da roda (~501px, o
// ring-outer do Figma); este viewBox é 0..200. Converte design → viewBox.
const LAYOUT_SCALE = 200 / 501.336; // ≈ 0.4
// Gradiente radial por fatia (aro escuro -> centro vivo), como no Figma — no lugar
// do preenchimento chapado bicolor. As fatias alternadas ganham um tom mais claro.
const RIM_DARKEN = 0.16;
const MID_LIGHTEN = 0.04;
const CORE_LIGHTEN = 0.3;
const ALT_LIGHTEN = 0.12;
// Glow branco-azulado no centro (atrás do botão), como no Figma.
const GLOW_RADIUS = 46;
// Estrela de brilho central: raio da ponta (no centro da fatia) e do vale (na
// divisória). O gradiente do brilho cobre até a ponta.
const SHINE_OUTER = 62;
const SHINE_INNER = 20;
const FULL_SPINS = 6;
// Duração do giro: vem do core (spin_duration_ms); fallback 4500ms.
const SPIN_DURATION_MS = 4500;
// Easing do giro — fonte única: alimenta a animação (WAAPI) e o cálculo do
// ângulo no tick (matemática, sem getComputedStyle por frame).
const SPIN_EASING = [0.16, 1, 0.3, 1] as const;

// Cor base das fatias quando o core não envia slice_bg.
const SLICE_FALLBACK = '#4f46e5';

// Espessura fixa da borda (aro visível além das fatias).
const BORDER_PX = 20;

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/roleta/${file}`);

// Cor do aro: ring_custom.border_color do core quando enviado; senão cai no
// slice_bg do prêmio. Alimenta o gradiente do aro.
const borderLight = computed(
  () => ringCustom?.borderColor || prizes[0]?.colors?.sliceBg || SLICE_FALLBACK,
);

// Aro externo (disco): gradiente vertical de 2 cores do core (topo -> base), que
// dá a profundidade/sombra dos lados. base cai numa versão mais escura do topo.
const borderBottom = computed(
  () => ringCustom?.borderColorBottom || darken(borderLight.value, 0.4),
);

const plateStyle = computed(() => ({
  background: `linear-gradient(180deg, ${borderLight.value} 0%, ${borderBottom.value} 100%)`,
}));

// Aro interno (mais escuro): ring_custom.border_inner_color quando enviado; senão
// uma versão mais escura do aro. Fica entre o aro externo e as fatias.
const innerBorderColor = computed(
  () => ringCustom?.borderInnerColor || darken(borderLight.value, 0.5),
);

const innerStyle = computed(() => ({
  inset: `${BORDER_PX * 0.6}px`,
  background: `linear-gradient(180deg, ${innerBorderColor.value} 0%, #141414 60%)`,
}));

// Risco branco (Ellipse 81 do Figma): filete fino no meio da faixa colorida,
// entre o aro externo e o interno. Destaque fixo (não vem do core).
const aroLineStyle = computed(() => ({ inset: `${BORDER_PX * 0.32}px` }));

// Avaliador de cubic-bezier(x1,y1,x2,y2) — a MESMA curva passada à animação:
// resolve t pelo eixo X (Newton com fallback de bisseção) e devolve o Y.
// Permite calcular o ângulo do rotor no tick por matemática pura.
function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const sampleDX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  return (x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    let t = x;

    for (let i = 0; i < 8; i += 1) {
      const err = sampleX(t) - x;

      if (Math.abs(err) < 1e-5) return sampleY(t);

      const d = sampleDX(t);

      if (Math.abs(d) < 1e-6) break;

      t -= err / d;
    }

    let lo = 0;
    let hi = 1;

    while (hi - lo > 1e-5) {
      t = (lo + hi) / 2;

      if (sampleX(t) < x) lo = t;
      else hi = t;
    }

    return sampleY(t);
  };
}

const spinEase = cubicBezier(...SPIN_EASING);

const rotor = ref<HTMLElement | null>(null);
// Rotação do container animada pela Web Animations API (não sofre com o reset
// global de `transition` do main.scss).
const rotation = ref(0);

const sliceAngle = computed(() => (prizes.length ? 360 / prizes.length : 360));

function polarToCartesian(angleDeg: number, radius: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;

  return {
    x: CENTER + radius * Math.cos(a),
    y: CENTER + radius * Math.sin(a),
  };
}

// Cunha "pra cima" (centrada no topo) reutilizada por todas as fatias — cada
// fatia só a rotaciona por `mid`. Mantém tudo concêntrico e barato.
const wedgePath = computed(() => {
  const half = sliceAngle.value / 2;
  const p1 = polarToCartesian(-half, SLICE_RADIUS);
  const p2 = polarToCartesian(half, SLICE_RADIUS);
  const largeArc = sliceAngle.value > 180 ? 1 : 0;

  return `M ${CENTER} ${CENTER} L ${p1.x} ${p1.y} A ${SLICE_RADIUS} ${SLICE_RADIUS} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
});

// Estrela de brilho central alinhada às fatias: 1 ponta por prêmio (no centro da
// fatia) e o vale na divisória. Fica dentro do rotor, então gira junto e sempre
// acompanha as fatias.
const shineStarPath = computed(() => {
  const n = prizes.length || 1;
  const step = 360 / n;
  let d = '';

  for (let i = 0; i < n; i += 1) {
    const tip = polarToCartesian(i * step, SHINE_OUTER);
    const valley = polarToCartesian(i * step + step / 2, SHINE_INNER);

    d += `${i === 0 ? 'M' : 'L'} ${tip.x} ${tip.y} L ${valley.x} ${valley.y} `;
  }

  return `${d}Z`;
});

// Quando o core manda a MESMA cor pra todas as fatias, o front sintetiza o
// dois-tons (alterna um tom mais claro). Quando manda cores distintas por prêmio
// (ex.: ouro/preto do VIP), usa cada cor como veio.
const uniformSlices = computed(() =>
  prizes.every((p) => p.colors.sliceBg === prizes[0]?.colors.sliceBg),
);

const slices = computed(() =>
  prizes.map((prize, i) => {
    // Fatia i centrada no topo (fatia 0 alinhada ao ponteiro). O grupo inteiro
    // gira por `mid` em torno do CENTRO — cunha, imagem e texto acompanham a
    // fatia (radial), como no Figma.
    const mid = i * sliceAngle.value;
    const raw = prize.colors.sliceBg || SLICE_FALLBACK;
    const base =
      uniformSlices.value && i % 2 === 1 ? lighten(raw, ALT_LIGHTEN) : raw;

    // Layout por prêmio vindo do core: tamanho de fonte (viewBox 0..200), recuo
    // do texto/imagem e escala da imagem (% da base). Defaults do mapper mantêm
    // o visual anterior quando o core não envia.
    const { layout } = prize;
    const titleFs = layout.textScale * LAYOUT_SCALE;
    const descFs = layout.text2Scale * LAYOUT_SCALE;
    // Gap entre as duas linhas escala com as fontes.
    const descGap = (titleFs + descFs) / 2;
    const titleX = CENTER + layout.textOffsetX * LAYOUT_SCALE;
    const titleY = CENTER - TITLE_RADIUS + layout.textOffsetY * LAYOUT_SCALE;
    const imgW = IMAGE_W * (layout.imageScale / 100);
    const imgH = IMAGE_H * (layout.imageScale / 100);

    return {
      prize,
      hasImage: !!prize.sliceImageUrl,
      desc: (prize.text2 || '').toUpperCase(),
      groupTransform: `rotate(${mid} ${CENTER} ${CENTER})`,
      gradientId: `rlt-slice-${i}`,
      coreColor: lighten(base, CORE_LIGHTEN),
      midColor: lighten(base, MID_LIGHTEN),
      rimColor: darken(base, RIM_DARKEN),
      titleColor: prize.colors.titleBg || '#ffffff',
      titleFs,
      descFs,
      titleX,
      titleY,
      descY: titleY + descGap,
      imgW,
      imgH,
      imageX: CENTER - imgW / 2 + layout.imageOffsetX * LAYOUT_SCALE,
      imageY:
        CENTER - IMAGE_RADIUS - imgH / 2 + layout.imageOffsetY * LAYOUT_SCALE,
    };
  }),
);

// Anima a roda até centralizar a fatia `position` no ponteiro (topo). Resolve
// quando a animação termina, para o chamador abrir o popup só depois do giro.
async function spinTo(position: number): Promise<void> {
  const n = prizes.length;

  if (!n || !rotor.value) return;

  // Centro da fatia `position` (agora coincide com i * sliceAngle).
  const center = position * sliceAngle.value;
  const desired = (((360 - center) % 360) + 360) % 360;
  const from = rotation.value;
  const delta = (((desired - (from % 360)) % 360) + 360) % 360;
  const to = from + FULL_SPINS * 360 + delta;

  const duration = spinDurationMs ?? SPIN_DURATION_MS;

  const animation = rotor.value.animate(
    [{ transform: `rotate(${from}deg)` }, { transform: `rotate(${to}deg)` }],
    {
      duration,
      easing: `cubic-bezier(${SPIN_EASING.join(', ')})`,
      fill: 'forwards',
    },
  );

  // "Clac": calcula o ângulo do rotor por matemática (mesmo cubic-bezier da
  // animação) e emite um tick quando cruza uma divisória (a meio-passo —
  // 360/n/2 — dos centros das fatias). Sem getComputedStyle no meio do giro.
  const slice = sliceAngle.value;
  const half = slice / 2;
  let lastEdge = Number.NaN;
  const currentAngle = () => {
    const t = Math.min(1, Number(animation.currentTime ?? 0) / duration);
    const deg = from + (to - from) * spinEase(t);

    return (((deg + half) % 360) + 360) % 360;
  };

  const tickLoop = () => {
    if (animation.playState !== 'running') return;

    const edge = Math.floor(currentAngle() / slice);

    if (edge !== lastEdge) {
      if (!Number.isNaN(lastEdge)) emit('tick');
      lastEdge = edge;
    }

    requestAnimationFrame(tickLoop);
  };

  requestAnimationFrame(tickLoop);

  await animation.finished;

  rotation.value = to % 360;
  animation.cancel();
}

defineExpose({ spinTo });
</script>

<template>
  <div class="wheel">
    <!-- Aro externo (disco): gradiente da cor da borda (do core) -> escuro. O
         glow de fundo fica no RouletteGame, pra cobrir atrás da tagline. -->
    <div
      class="wheel__plate"
      :style="plateStyle"
    />

    <!-- Aro interno (mais escuro): anel entre o aro externo e as fatias -->
    <div
      class="wheel__aro-inner"
      :style="innerStyle"
    />

    <!-- Risco branco (Ellipse 81): filete no meio da faixa colorida do aro -->
    <div
      class="wheel__aro-line"
      :style="aroLineStyle"
    />

    <!-- Disco das fatias girando -->
    <div
      ref="rotor"
      class="wheel__rotor"
      :style="{
        inset: `${BORDER_PX}px`,
        transform: `rotate(${rotation}deg)`,
      }"
    >
      <svg
        class="wheel__svg"
        viewBox="0 0 200 200"
      >
        <defs>
          <clipPath id="rlt-wedge-clip">
            <path :d="wedgePath" />
          </clipPath>

          <!-- Gradiente radial por fatia: aro escuro -> centro vivo. userSpaceOnUse
               fixa o centro em (CENTER, CENTER) p/ todas ficarem concêntricas. -->
          <radialGradient
            v-for="slice in slices"
            :id="slice.gradientId"
            :key="`g-${slice.prize.id}`"
            :cx="CENTER"
            :cy="CENTER"
            gradientUnits="userSpaceOnUse"
            r="100"
          >
            <stop
              offset="0"
              :stop-color="slice.coreColor"
            />
            <stop
              offset="0.5"
              :stop-color="slice.midColor"
            />
            <stop
              offset="1"
              :stop-color="slice.rimColor"
            />
          </radialGradient>

          <radialGradient
            id="rlt-core-glow"
            :cx="CENTER"
            :cy="CENTER"
            gradientUnits="userSpaceOnUse"
            :r="GLOW_RADIUS"
          >
            <stop
              offset="0"
              stop-color="rgba(198, 206, 255, 0.55)"
            />
            <stop
              offset="0.45"
              stop-color="rgba(120, 120, 255, 0.14)"
            />
            <stop
              offset="1"
              stop-color="rgba(120, 120, 255, 0)"
            />
          </radialGradient>

          <!-- Brilho da estrela central (raios convergindo): claro no centro. -->
          <radialGradient
            id="rlt-shine"
            :cx="CENTER"
            :cy="CENTER"
            gradientUnits="userSpaceOnUse"
            :r="SHINE_OUTER"
          >
            <stop
              offset="0"
              stop-color="rgba(255, 255, 255, 0.38)"
            />
            <stop
              offset="0.55"
              stop-color="rgba(255, 255, 255, 0.1)"
            />
            <stop
              offset="1"
              stop-color="rgba(255, 255, 255, 0)"
            />
          </radialGradient>
        </defs>

        <!-- 1) Cunhas (gradiente radial) -->
        <g
          v-for="slice in slices"
          :key="`w-${slice.prize.id}`"
          :transform="slice.groupTransform"
        >
          <path
            :d="wedgePath"
            :fill="`url(#${slice.gradientId})`"
            stroke="#ffffff2e"
            stroke-width="0.5"
          />
        </g>

        <!-- 2) Glow central branco-azulado (sobre as cunhas, sob o conteúdo) -->
        <circle
          :cx="CENTER"
          :cy="CENTER"
          fill="url(#rlt-core-glow)"
          :r="GLOW_RADIUS"
        />

        <!-- 2b) Estrela de brilho: 1 ponta por fatia (alinhada aos prêmios) -->
        <path
          :d="shineStarPath"
          fill="url(#rlt-shine)"
        />

        <!-- 3) Imagens + textos por fatia -->
        <g
          v-for="slice in slices"
          :key="`c-${slice.prize.id}`"
          :transform="slice.groupTransform"
        >
          <g
            v-if="slice.hasImage"
            clip-path="url(#rlt-wedge-clip)"
          >
            <image
              :height="slice.imgH"
              :href="slice.prize.sliceImageUrl"
              preserveAspectRatio="xMidYMid meet"
              :width="slice.imgW"
              :x="slice.imageX"
              :y="slice.imageY"
            />
          </g>

          <text
            class="wheel__title"
            dominant-baseline="middle"
            :fill="slice.titleColor"
            :style="{ fontSize: `${slice.titleFs}px` }"
            text-anchor="middle"
            :x="slice.titleX"
            :y="slice.titleY"
          >
            {{ slice.prize.text1 }}
          </text>

          <text
            v-if="slice.desc"
            class="wheel__desc"
            dominant-baseline="middle"
            :fill="slice.titleColor"
            :style="{ fontSize: `${slice.descFs}px` }"
            text-anchor="middle"
            :x="slice.titleX"
            :y="slice.descY"
          >
            {{ slice.desc }}
          </text>
        </g>
      </svg>
    </div>

    <!-- Estrelas decorativas (frame "lights" do Figma): PNG estático no arco
         superior, no lugar das antigas estrelinhas que giravam com o disco. -->
    <img
      alt=""
      class="wheel__stars"
      :src="asset('stars.png')"
    />

    <!-- Botão central (GIRAR/PLAY), fornecido pelo pai -->
    <div class="wheel__center">
      <slot name="center" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.wheel {
  @apply relative mx-auto;

  aspect-ratio: 1 / 1;
}

// Aro (disco): círculo com o gradiente da cor da borda; o filete branco e a
// sombra externa acompanham o círculo via box-shadow (borda arredondada).
.wheel__plate {
  @apply absolute inset-0 w-full h-full pointer-events-none select-none rounded-full;

  z-index: 1;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5);
}

// Aro interno (mais escuro): inset via :style, por cima do aro externo e sob as
// fatias.
.wheel__aro-inner {
  @apply absolute pointer-events-none select-none rounded-full;

  z-index: 1;
}

// Risco branco (Ellipse 81): filete de 1px no meio da faixa colorida.
.wheel__aro-line {
  @apply absolute pointer-events-none select-none rounded-full;

  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.wheel__rotor {
  @apply absolute rounded-full;

  transform-origin: center center;
  will-change: transform;
  z-index: 2;
}

.wheel__svg {
  @apply w-full h-full rounded-full block;
}

// Estrelas do frame "lights" do Figma: posição/tamanho relativos à roda
// (left/top/width do frame ÷ 501,336). Estáticas, acima das fatias.
.wheel__stars {
  @apply absolute pointer-events-none select-none;

  left: 7.92%;
  top: 0%;
  width: 84.4%;
  height: auto;
  z-index: 4;
}

// font-size vem do core por prêmio (inline :style); aqui só família/peso.
.wheel__title,
.wheel__desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.wheel__center {
  @apply absolute left-1/2 top-1/2;

  transform: translate(-50%, -50%);
  z-index: 5;
}
</style>
